import { useCallback, useRef, useEffect } from 'react';

export function useDebouncedCallback<T extends (...args: any[]) => any>(
  callback: T,
  delay: number,
  deps: React.DependencyList = []
): T {
  const callbackRef = useRef(callback);
  callbackRef.current = callback;

  const timeoutRef = useRef<NodeJS.Timeout>();

  const debouncedCallback = useCallback(
    (...args: Parameters<T>) => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }

      timeoutRef.current = setTimeout(() => {
        callbackRef.current(...args);
      }, delay);
    },
    [delay, ...deps]
  );

  useEffect(() => {
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, []);

  return debouncedCallback as T;
}

// Usage for viewport updates
export function useViewportUpdates() {
  const updateViewport = useAppStore((state) => state.updateViewport);
  
  const debouncedUpdateViewport = useDebouncedCallback(
    (viewport: Partial<ViewportState>) => {
      updateViewport(viewport);
      // Also save to database after viewport changes
      // saveViewportToDatabase(viewport);
    },
    300
  );

  return { updateViewport: debouncedUpdateViewport };
}
